import SimpleArtist from '@common/types/artist/simple-artist';

import { CallApi, handleJSONResponse, handleResponse } from './common';
import { uploadToS3, startUpload, UploadStartRequest } from './upload';
import { ArtistInviteEvent } from '@services/artist/types/artist-invite-event';
import { NewInstance } from '@common/types/data/new-instance';
import { ArtistPage } from '@services/artist/types/artist-page';
import { ArtistSearchResponse } from '@services/artist/types/artist-search-response';
import SimpleUser from '../../../../common/types/user/simple-user';
import Achievements from '../../../../common/types/artist/achievements';
import { AssetUploadResponse } from '@webapp/sdk/assets';
import { ArtistRelation } from '@common/types/artist/artist-relation';

const callAPI = CallApi('artists');

export const listMyArtists = async () => {
  const response = await callAPI('/', 'GET');

  return handleJSONResponse(response);
};

export const create = async (
  profile: NewInstance<SimpleArtist>,
  onboardingId: string
): Promise<SimpleArtist> => {
  const response = await callAPI('/', 'POST', { profile, onboardingId });

  return await handleJSONResponse(response);
};

export const deactivate = async (artistId: string): Promise<string> => {
  const response = await callAPI(`/${artistId}/deactivate`, 'DELETE');

  return await handleResponse(response);
};

export const getTeam = async (artistId: string): Promise<SimpleUser[]> => {
  const response = await callAPI(`/${artistId}/team`, 'GET');

  return await handleJSONResponse(response);
};

export const removeFromTeam = async (
  artistId: string,
  userId: string
): Promise<SimpleUser[]> => {
  const response = await callAPI(`/${artistId}/team/${userId}`, 'DELETE');

  return await handleJSONResponse(response);
};

export const search = async (
  artistName: string
): Promise<ArtistSearchResponse> => {
  const response = await callAPI('/search', 'GET', { q: artistName });

  return await handleJSONResponse(response);
};

export const update = async (profile: SimpleArtist): Promise<SimpleArtist> => {
  const response = await callAPI(`/${profile.id}`, 'PUT', profile);

  return await handleJSONResponse(response);
};

export const updatePlatforms = async (
  artistId: string,
  platforms: SimpleArtist['platforms']
) => {
  const response = await callAPI(`/${artistId}/platforms`, 'PUT', platforms);

  return await handleJSONResponse(response);
};

export const uploadImage = async (
  file: File,
  type: string
): Promise<AssetUploadResponse> => {
  const form: UploadStartRequest = {
    filename: file.name,
    contentType: file.type,
  };

  const { formData, ...data } = await startUpload<{}, AssetUploadResponse>(
    form,
    callAPI,
    `/images/${type}`
  );

  return {
    ...data,
    upload: uploadToS3(file, formData),
  };
};

export const getPage = async (artistId: string): Promise<ArtistPage> => {
  const response = await callAPI(`/page/${artistId}`, 'GET');

  return await handleJSONResponse(response);
};

export const updatePage = async (
  artist: SimpleArtist,
  page: ArtistPage
): Promise<ArtistPage> => {
  const publishedPage: ArtistPage = {
    ...page,
    artist,
  };

  const response = await callAPI(`/page/${artist.id}`, 'PUT', publishedPage);

  return await handleJSONResponse(response);
};

export const publishPage = async (
  artist: SimpleArtist,
  page: ArtistPage
): Promise<ArtistPage> => {
  const publishedPage: ArtistPage = {
    ...page,
    artist,
  };

  const response = await callAPI(`/page/${artist.id}`, 'POST', publishedPage);

  return await handleJSONResponse(response);
};

export const getAchievements = async (): Promise<Achievements[]> => {
  const response = await callAPI(`/achievements`, 'GET');

  return await handleJSONResponse(response);
};

export const updateAchievement = async (
  artistId: string,
  achievementId: string
) => {
  const response = await callAPI(
    `/${artistId}/achievement/${achievementId}`,
    'PUT'
  );

  return await handleJSONResponse(response);
};

export const switchTeamRole = async (
  artistId: string,
  teamMemberId: string,
  role: ArtistRelation['role']
) => {
  const response = await callAPI(`/team/${artistId}`, 'PUT', {
    id: teamMemberId,
    role,
  });

  return await handleJSONResponse(response);
};

export const setLastAccess = async (artistId: string) => {
  const response = await callAPI(`/${artistId}/switch`, 'PUT');

  return await handleJSONResponse(response);
};

export const restoreArtist = async (
  artistId: string,
  releaseIds?: string[]
) => {
  const response = await callAPI(`/${artistId}/restore`, 'POST', {
    releaseIds,
  });

  return await handleJSONResponse(response);
};

export const inviteArtist = async (
  createRequest: ArtistInviteEvent
): Promise<SimpleArtist> => {
  const response = await callAPI(`/invite`, 'POST', createRequest);
  return await handleJSONResponse(response);
};

export const validateExternalId = async (
  externalId: string
): Promise<{ isValidated: boolean }> => {
  const response = await callAPI(`/validate/${externalId}`, 'GET');
  return await handleJSONResponse(response);
};
